import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LogOut,
  Menu,
  X,
  User,
  ShoppingBag,
  ExternalLink,
} from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { useFeatures } from '../context/FeatureContext';
import { Button } from './ui/button';
import { CompactFooter } from './Footer';
import GlobalSearch from './GlobalSearch';
import { ViewModeSwitcher } from './ViewModeSwitcher';
import { BottomNav } from './BottomNav';
import { InstallPrompt } from './InstallPrompt';
import { OfflineIndicator } from './OfflineIndicator';
import { NotificationPrompt } from './NotificationPrompt';

import { NavGroup, NavItem } from './layout/NavItems';
import { getNavForRole, filterByFeatures } from './layout/navConfig';
import { useDashboardBadges } from './layout/useDashboardBadges';
import { useBusinessLogo } from './layout/useBusinessLogo';
import { buildHandoffUrl, isPreviewHost, normHost } from '../utils/crossHostAuth';

const ROLE_HOME = {
  admin: '/admin',
  coach: '/coach',
  staff: '/staff',
  customer: '/customer/lanes',
  player: '/player',
};

export const DashboardLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  // Signed-in portal pages never belong in search results.
  useEffect(() => {
    let m = document.head.querySelector('meta[name="robots"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'robots'); document.head.appendChild(m); }
    m.setAttribute('content', 'noindex, nofollow');
  }, []);
  const { branding } = useTenant();
  const tenantName = branding?.business_name || 'Cricket Academy';
  const { isFeatureEnabled, isPlanFeatureEnabled } = useFeatures();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const role = user?.role;
  const isAdmin = role === 'admin';
  const isStaff = role === 'staff';
  const showGlobalSearch = isAdmin || isStaff;

  // iter309g — Saved-session cross-host redirect. The /login redirect
  // in Login.js only catches fresh logins; users who already had a
  // valid token (saved session, PWA opened from home screen, etc.)
  // and navigate directly to /admin on the platform host would stay
  // on my.vyomos.io with broken branding + cross-tenant data risk.
  // This effect runs once on layout mount, fetches /api/tenant/me,
  // and bounces to the tenant's canonical host when there's a mismatch.
  //
  // iter312 — Cross-host token handoff: passes the JWT via URL
  // fragment so the destination host (different origin → separate
  // localStorage) can pick up the session instead of forcing a
  // re-login. Also skips preview/localhost so developer iteration
  // isn't bounced to the prod tenant host.
  useEffect(() => {
    if (!user || role === 'super_admin') return;
    if (typeof window === 'undefined') return;
    if (isPreviewHost()) return; // never auto-bounce off the preview env
    const ctrl = new AbortController();
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch('/api/tenant/me', {
          headers: { Authorization: `Bearer ${token}` },
          signal: ctrl.signal,
        });
        if (!res.ok) return;
        const me = await res.json();
        const targetHost = me.canonical_hostname || me.custom_domain || me.subdomain_host;
        if (!targetHost) return;
        if (normHost(targetHost) === normHost(window.location.host)) return;
        // Don't loop: the target host must look "valid" (contains a dot).
        if (!targetHost.includes('.')) return;
        const url = buildHandoffUrl(
          targetHost,
          window.location.pathname,
          window.location.search,
        );
        // Replace, not push, so the back button doesn't trap the user
        // on the wrong host.
        window.location.replace(url);
      } catch {/* network blip — leave user where they are */}
    })();
    return () => ctrl.abort();
  }, [user?.id]);

  const { notificationCount, chatUnreadCount, whatsappUnread } = useDashboardBadges({ role, whatsappEnabled: isPlanFeatureEnabled('comms.whatsapp') });
  const { logoUrl, sizeClass, alignmentClass } = useBusinessLogo();

  const navItems = filterByFeatures(getNavForRole(role), { isAdmin, isFeatureEnabled, isPlanFeatureEnabled });
  const handleItemClick = () => setSidebarOpen(false);

  const renderNavEntry = (item) => {
    if (item.type === 'group') {
      return (
        <NavGroup
          key={item.label}
          group={item}
          location={location}
          onItemClick={handleItemClick}
          waBadgeCount={whatsappUnread}
          chatBadgeCount={chatUnreadCount}
        />
      );
    }
    return (
      <NavItem
        key={item.path}
        item={item}
        location={location}
        onItemClick={handleItemClick}
        notificationCount={notificationCount}
        chatBadgeCount={chatUnreadCount}
        waBadgeCount={whatsappUnread}
      />
    );
  };

  return (
    <div className="min-h-screen bg-theme-bg" data-themed="true">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="bg-theme-surface shadow-md"
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-40 h-full w-64 bg-theme-surface text-theme-muted
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`p-4 border-b border-theme-muted/20 flex ${alignmentClass}`}>
            <Link
              to={ROLE_HOME[role] || '/parent'}
              className="inline-flex items-center gap-3"
              data-testid="logo-link"
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={tenantName}
                  className={`${sizeClass} w-auto`}
                />
              ) : (
                <span className="text-theme-text font-bold text-lg">{tenantName}</span>
              )}
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
            {navItems.map(renderNavEntry)}
          </nav>

          {/* User info */}
          <div className="p-4 pb-24 lg:pb-4 border-t border-theme-muted/20">
            {/* iter323 — Generic external link configurable per tenant.
                Pre-iter322 this was hardcoded to dascricket.com merch.
                iter322 made it conditional on `branding.merch_store_url`.
                iter323 generalises to `branding.external_link_url` +
                `branding.external_link_label` so each tenant can point
                it at whatever they want (shop, donation page, parent
                resources, social feed, etc.). Both fields must be set
                for the link to render. */}
            {branding?.external_link_url && branding?.external_link_label && (
              <a
                href={branding.external_link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 mb-3 rounded-md bg-theme-accent/10 border border-theme-accent/30 text-theme-accent hover:bg-theme-accent/20 transition-colors duration-200"
                data-testid="tenant-external-link"
              >
                <ShoppingBag className="h-5 w-5" />
                <span className="font-medium text-sm truncate">{branding.external_link_label}</span>
                <ExternalLink className="h-4 w-4 ml-auto shrink-0" />
              </a>
            )}

            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-theme-primary/20 rounded-full flex items-center justify-center">
                <span className="text-theme-primary font-semibold">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-theme-text font-medium truncate">{user?.name}</p>
                <p className="text-xs text-theme-muted capitalize">{role}</p>
              </div>
            </div>
            <Link to="/profile">
              <Button
                variant="ghost"
                className="w-full justify-start text-theme-muted hover:text-theme-text hover:bg-theme-bg"
                data-testid="profile-btn"
              >
                <User className="h-4 w-4 mr-2" />
                My Profile
              </Button>
            </Link>
            <Button
              variant="ghost"
              className="w-full justify-start text-theme-muted hover:text-theme-text hover:bg-theme-bg"
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Backdrop for mobile */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen flex flex-col">
        <header className="glass-header sticky top-0 z-20 px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3 sm:gap-4">
            <h1 className="min-w-0 flex-1 font-heading text-xl font-bold text-theme-text ml-12 truncate lg:ml-0 lg:text-3xl">
              {title}
            </h1>
            <div className="ml-auto flex shrink-0 items-center gap-3">
              {showGlobalSearch && (
                <div className="hidden md:block flex-1 max-w-md">
                  <GlobalSearch />
                </div>
              )}
              <ViewModeSwitcher />
            </div>
          </div>
        </header>

        {/* Content - add bottom padding on mobile for bottom nav */}
        <div className="p-4 lg:p-8 flex-1 pb-24 lg:pb-8">{children}</div>

        {/* Footer - hidden on mobile where bottom nav is shown */}
        <CompactFooter className="hidden lg:block" />
      </main>

      {/* Mobile Bottom Navigation */}
      <BottomNav notificationCount={notificationCount} />
      <InstallPrompt />
      <OfflineIndicator />
      <NotificationPrompt />
    </div>
  );
};
